import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Layout } from '../../components/layout/Layout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { RadioGroup, RadioGroupItem } from '../../components/ui/radio-group';
import { PhoneInput } from '../../components/ui/phone-input';
import { toast } from 'sonner';
import { Ticket, Lock, User, Loader2, Eye, EyeOff, MessageSquare, ArrowLeft, CheckCircle, Mail, Phone } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

export default function Register() {
  const [signupMethod, setSignupMethod] = useState(null); // null = choose, 'phone', 'email'
  const [step, setStep] = useState(1); // 1: Enter identifier, 2: OTP verification, 3: Complete registration
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    countryCode: '+234',
    email: '',
    password: '',
    confirmPassword: '',
    role: 'attendee',
    otpCode: '',
    otpVerified: false,
    verificationToken: null
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  // ===== Phone OTP =====
  const handleSendPhoneOTP = async () => {
    if (!formData.phone || formData.phone.length < 7) {
      toast.error('Please enter a valid phone number');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/send-otp`, {
        phone: formData.phone,
        country_code: formData.countryCode
      });
      setStep(2);
      toast.success(res.data?.channel === 'whatsapp' ? 'OTP sent via WhatsApp!' : 'OTP sent! Check your phone');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to send OTP';
      if (errorMsg.includes('already registered')) {
        toast.error('This phone number is already registered. Please login instead.');
      } else if (errorMsg.includes('not configured')) {
        toast.info('Proceeding without OTP verification');
        setStep(3);
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendPhoneOTP = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_URL}/api/auth/send-otp`, {
        phone: formData.phone,
        country_code: formData.countryCode
      });
      toast.success(res.data?.channel === 'whatsapp' ? 'New OTP sent via WhatsApp!' : 'New OTP sent!');
    } catch {
      toast.error('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPhoneOTP = async () => {
    if (!formData.otpCode || formData.otpCode.length < 4) {
      toast.error('Please enter the verification code');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-otp`, {
        phone: formData.phone,
        country_code: formData.countryCode,
        code: formData.otpCode
      });
      if (response.data.valid) {
        setFormData(prev => ({ ...prev, otpVerified: true, verificationToken: response.data.verification_token }));
        setStep(3);
        toast.success('Phone verified!');
      } else {
        toast.error('Invalid code. Please try again.');
      }
    } catch {
      toast.error('Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ===== Email OTP =====
  const handleSendEmailOTP = async () => {
    if (!formData.email || !formData.email.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/email/send-otp`, { email: formData.email });
      setStep(2);
      toast.success('Verification code sent to your email!');
    } catch (error) {
      const status = error.response?.status;
      const errorMsg = error.response?.data?.detail || '';
      if (status === 422) {
        toast.error('Please enter a valid email address.');
      } else if (errorMsg.includes('already registered')) {
        toast.error('An account with this email already exists. Try logging in instead.');
      } else {
        toast.error('Unable to send code. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmailOTP = async () => {
    setLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/email/send-otp`, { email: formData.email });
      toast.success('New code sent!');
    } catch {
      toast.error('Unable to send code. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyEmailOTP = async () => {
    if (!formData.otpCode || formData.otpCode.length < 4) {
      toast.error('Please enter the verification code');
      return;
    }
    setLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/auth/email/verify-otp`, {
        email: formData.email,
        code: formData.otpCode
      });
      if (response.data.valid) {
        setFormData(prev => ({ ...prev, otpVerified: true, verificationToken: response.data.verification_token }));
        setStep(3);
        toast.success('Email verified!');
      } else {
        toast.error('Invalid code. Please try again.');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Verification failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ===== Submit Registration =====
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!formData.otpVerified) {
      toast.error(`Please verify your ${signupMethod === 'email' ? 'email' : 'phone number'} first`);
      setStep(2);
      return;
    }

    setLoading(true);
    try {
      await register({
        signupMethod,
        name: formData.name,
        phone: formData.phone,
        countryCode: formData.countryCode,
        email: formData.email || null,
        password: formData.password,
        role: formData.role,
        verificationToken: formData.verificationToken
      });
      if (formData.role === 'organizer') {
        toast.success('Account created successfully!');
        navigate('/dashboard');
      } else {
        toast.success('Account created successfully!');
        navigate('/');
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Registration failed';
      if (errorMsg.includes('verification') || errorMsg.includes('expired')) {
        toast.error('Verification expired. Please verify again.');
        setStep(1);
        setFormData(prev => ({ ...prev, otpCode: '', otpVerified: false, verificationToken: null }));
      } else if (errorMsg.includes('already registered')) {
        toast.error(errorMsg);
        navigate('/login');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setLoading(false);
    }
  };

  // ===== Render: Method Chooser =====
  const renderMethodChooser = () => (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground text-center mb-2">Choose how you'd like to sign up</p>
      <Button
        type="button"
        variant="outline"
        className="w-full h-14 justify-start gap-3 rounded-xl text-base"
        onClick={() => { setSignupMethod('phone'); setStep(1); }}
        data-testid="signup-method-phone"
      >
        <div className="p-2 rounded-lg bg-primary/10"><Phone className="w-5 h-5 text-primary" /></div>
        Sign up with phone number
      </Button>
      <Button
        type="button"
        variant="outline"
        className="w-full h-14 justify-start gap-3 rounded-xl text-base"
        onClick={() => { setSignupMethod('email'); setStep(1); }}
        data-testid="signup-method-email"
      >
        <div className="p-2 rounded-lg bg-primary/10"><Mail className="w-5 h-5 text-primary" /></div>
        Sign up with email
      </Button>
    </div>
  );

  // ===== Render: Step 1 =====
  const renderStep1 = () => {
    if (signupMethod === 'email') {
      return (
        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address</Label>
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="you@example.com"
                value={formData.email}
                onChange={handleChange}
                required
                className="pl-10 h-12 rounded-xl"
                data-testid="register-email"
              />
            </div>
            <p className="text-xs text-muted-foreground">We'll send you a verification code</p>
          </div>
          <Button
            type="button"
            onClick={handleSendEmailOTP}
            className="w-full btn-primary"
            disabled={loading || !formData.email}
            data-testid="send-email-otp-btn"
          >
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending code...</> : <><Mail className="w-4 h-4 mr-2" />Send verification code</>}
          </Button>
        </div>
      );
    }
    // Phone
    return (
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="phone">Phone Number</Label>
          <PhoneInput
            value={formData.phone}
            countryCode={formData.countryCode}
            onValueChange={(phone) => setFormData({ ...formData, phone })}
            onCountryCodeChange={(countryCode) => setFormData({ ...formData, countryCode })}
            placeholder="8012345678"
            required
            data-testid="register-phone"
          />
          <p className="text-xs text-muted-foreground">We'll send you a verification code</p>
        </div>
        <Button
          type="button"
          onClick={handleSendPhoneOTP}
          className="w-full btn-primary"
          disabled={loading || !formData.phone}
          data-testid="send-otp-btn"
        >
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Sending OTP...</> : <><MessageSquare className="w-4 h-4 mr-2" />Send verification code</>}
        </Button>
      </div>
    );
  };

  // ===== Render: Step 2 (OTP entry) =====
  const renderStep2 = () => {
    const isEmail = signupMethod === 'email';
    const identifier = isEmail ? formData.email : `${formData.countryCode}${formData.phone}`;
    const IconComp = isEmail ? Mail : MessageSquare;
    return (
      <div className="space-y-4">
        <div className="text-center mb-4">
          <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
            <IconComp className="w-8 h-8 text-primary" />
          </div>
          <p className="text-sm text-muted-foreground">
            Enter the 6-digit code sent to<br />
            <span className="font-medium text-foreground">{identifier}</span>
          </p>
        </div>
        <div className="space-y-2">
          <Label htmlFor="otpCode">Verification Code</Label>
          <Input
            id="otpCode"
            name="otpCode"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
            placeholder="Enter 6-digit code"
            value={formData.otpCode}
            onChange={handleChange}
            maxLength={6}
            className="h-14 text-center text-2xl tracking-[0.5em] font-mono rounded-xl"
            style={{ fontSize: '24px' }}
            autoFocus
            data-testid="otp-input"
          />
        </div>
        <Button
          type="button"
          onClick={isEmail ? handleVerifyEmailOTP : handleVerifyPhoneOTP}
          className="w-full btn-primary"
          disabled={loading || formData.otpCode.length < 4}
          data-testid="verify-otp-btn"
        >
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Verifying...</> : 'Verify'}
        </Button>
        <div className="flex items-center justify-between text-sm">
          <button
            type="button"
            onClick={() => { setStep(1); setFormData(prev => ({ ...prev, otpCode: '' })); }}
            className="text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            {isEmail ? 'Change email' : 'Change number'}
          </button>
          <button
            type="button"
            onClick={isEmail ? handleResendEmailOTP : handleResendPhoneOTP}
            className="text-primary hover:underline"
            disabled={loading}
          >
            Resend code
          </button>
        </div>
      </div>
    );
  };

  // ===== Render: Step 3 (Complete profile) =====
  const renderStep3 = () => {
    const isEmail = signupMethod === 'email';
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center gap-2 p-3 rounded-lg bg-green-500/10 border border-green-500/20">
          <CheckCircle className="w-5 h-5 text-green-500" />
          <div className="flex-1">
            <p className="text-sm font-medium text-green-600">{isEmail ? 'Email verified' : 'Phone verified'}</p>
            <p className="text-xs text-muted-foreground">{isEmail ? formData.email : `${formData.countryCode}${formData.phone}`}</p>
          </div>
          <button
            type="button"
            onClick={() => { setStep(1); setFormData(prev => ({ ...prev, otpCode: '', otpVerified: false, verificationToken: null })); }}
            className="text-xs text-muted-foreground hover:text-foreground"
          >Change</button>
        </div>

        <div className="space-y-2">
          <Label htmlFor="name">Full Name</Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input id="name" name="name" type="text" placeholder="John Doe" value={formData.name} onChange={handleChange} required className="pl-10 h-12 rounded-xl" data-testid="register-name-input" />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="password">Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input id="password" name="password" type={showPassword ? "text" : "password"} placeholder="Min. 6 characters" value={formData.password} onChange={handleChange} required minLength={6} className="pl-10 pr-12 h-12 rounded-xl" data-testid="register-password-input" />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors" data-testid="toggle-password-visibility">
              {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="confirmPassword">Confirm Password</Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
            <Input id="confirmPassword" name="confirmPassword" type={showConfirmPassword ? "text" : "password"} placeholder="Confirm your password" value={formData.confirmPassword} onChange={handleChange} required className="pl-10 pr-12 h-12 rounded-xl" data-testid="register-confirm-password-input" />
            <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors">
              {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>

        <div className="space-y-3">
          <Label>I want to</Label>
          <RadioGroup value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })} className="grid grid-cols-1 gap-3">
            <Label htmlFor="attendee" className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${formData.role === 'attendee' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`} data-testid="role-attendee">
              <RadioGroupItem value="attendee" id="attendee" className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Ticket className={`w-4 h-4 ${formData.role === 'attendee' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className={`font-medium ${formData.role === 'attendee' ? 'text-primary' : ''}`}>Discover & Buy Tickets</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Browse events, purchase tickets, and attend amazing experiences</p>
              </div>
            </Label>
            <Label htmlFor="organizer" className={`flex items-start gap-3 p-4 rounded-xl border cursor-pointer transition-colors ${formData.role === 'organizer' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`} data-testid="role-organizer">
              <RadioGroupItem value="organizer" id="organizer" className="mt-1" />
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <User className={`w-4 h-4 ${formData.role === 'organizer' ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className={`font-medium ${formData.role === 'organizer' ? 'text-primary' : ''}`}>Host & Manage Events</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">Create events, sell tickets, and manage your attendees</p>
              </div>
            </Label>
          </RadioGroup>
        </div>

        <Button type="submit" className="w-full btn-primary" disabled={loading} data-testid="register-submit-btn">
          {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Creating account...</> : 'Create account'}
        </Button>
      </form>
    );
  };

  // ===== Page title/description =====
  const getTitle = () => {
    if (!signupMethod) return 'Create an account';
    if (step === 1) return signupMethod === 'email' ? 'Sign up with email' : 'Sign up with phone';
    if (step === 2) return signupMethod === 'email' ? 'Check your inbox' : 'Verify your phone';
    return 'Complete your profile';
  };
  const getDescription = () => {
    if (!signupMethod) return 'Join Next Gig to discover amazing events';
    if (step === 1) return signupMethod === 'email' ? 'Enter your email to get started' : 'Enter your phone number to get started';
    if (step === 2) return 'Enter the code we sent you';
    return 'Just a few more details';
  };
  const totalSteps = 3;
  const currentProgress = !signupMethod ? 0 : step;

  return (
    <Layout showFooter={false} showMobileNav={false}>
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
                <Ticket className="w-7 h-7 text-primary-foreground" />
              </div>
            </div>
            <CardTitle className="text-2xl">{getTitle()}</CardTitle>
            <CardDescription>{getDescription()}</CardDescription>

            {signupMethod && (
              <div className="flex items-center justify-center gap-2 mt-4">
                {[1, 2, 3].map((s) => (
                  <div key={s} className={`h-1.5 rounded-full transition-all ${s === currentProgress ? 'w-8 bg-primary' : s < currentProgress ? 'w-4 bg-primary/50' : 'w-4 bg-muted'}`} />
                ))}
              </div>
            )}
          </CardHeader>

          <CardContent className="space-y-4">
            {!signupMethod && renderMethodChooser()}
            {signupMethod && step === 1 && renderStep1()}
            {signupMethod && step === 2 && renderStep2()}
            {signupMethod && step === 3 && renderStep3()}

            {signupMethod && step === 1 && (
              <button
                type="button"
                onClick={() => { setSignupMethod(null); setFormData(prev => ({ ...prev, otpCode: '', otpVerified: false, verificationToken: null })); }}
                className="w-full text-center text-sm text-muted-foreground hover:text-foreground flex items-center justify-center gap-1"
                data-testid="change-signup-method"
              >
                <ArrowLeft className="w-3 h-3" />
                Use {signupMethod === 'email' ? 'phone number' : 'email'} instead
              </button>
            )}
          </CardContent>

          <CardFooter className="flex flex-col gap-4">
            <p className="text-xs text-muted-foreground text-center">
              By creating an account, you agree to our{' '}
              <Link to="/terms" className="text-primary hover:underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" className="text-primary hover:underline">Privacy Policy</Link>
            </p>
            <p className="text-sm text-muted-foreground text-center">
              Already have an account?{' '}
              <Link to="/login" className="text-primary hover:underline font-medium" data-testid="login-link">Sign in</Link>
            </p>
          </CardFooter>
        </Card>
      </div>
    </Layout>
  );
}
